import axios from 'axios';
import Cookies from 'js-cookie';
import api from './api';
import { isValidToken } from './jwt';
// config
import {
  CHATBOT_HOST_API,
  HOST_API,
  CHATBOT_API_KEY,
  PAYMENTS_HOST_API,
  ORIGINATION_HOST_API,
  PROSCAI_REPORTS_API_URL,
} from '../config';
// import useAuth from '../hooks/useAuth'

// ----------------------------------------------------------------------

export const axiosInstance = axios.create({
  baseURL: HOST_API,
});

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (
      (error?.response?.status === 401 || error?.response?.status === 403) &&
      error?.response?.data?.code === 'token_not_valid'
    ) {
      try {
        const refreshToken = Cookies.get('refreshToken');
        const isValid = isValidToken(refreshToken);

        if (!refreshToken || !isValid) throw new Error('refreshToken not found'); // if refreshToken is not found, redirect to login

        const { data } = await api.auth.refresh(refreshToken);

        if (data?.access) {
          Cookies.set('accessToken', data?.access, { secure: true });
        }
        if (data?.refresh) {
          Cookies.set('refreshToken', data?.refresh, { secure: true });
        }

        // Update the Authorization header with the new access token
        error.config.headers.Authorization = `Bearer ${data?.access}`;

        // Retry the original request with the new token
        return axiosInstance(error.config);
      } catch (refreshError) {
        Cookies.remove('refreshToken');
        Cookies.remove('accessToken');
        Cookies.remove('user');
        window.location.href = '/auth/login';
        throw refreshError;
      }
    }
    throw error?.response?.data ?? error;
  }
);

export const axiosPaymentsInstance = axios.create({
  baseURL: PAYMENTS_HOST_API,
});

export const axiosOriginationInstance = axios.create({
  baseURL: ORIGINATION_HOST_API,
});

export const axiosProscaiReportsInstance = axios.create({
  baseURL: PROSCAI_REPORTS_API_URL,
});

let mesaControlRefreshPromise = null;

const refreshMesaControlAccessToken = () => {
  if (mesaControlRefreshPromise) return mesaControlRefreshPromise;

  const refreshToken = Cookies.get('refreshToken');
  if (!refreshToken || !isValidToken(refreshToken)) {
    return Promise.reject(new Error('refreshToken not found'));
  }

  mesaControlRefreshPromise = axios
    .post(`${HOST_API}/api/v1/auth/refresh/`, { refresh: refreshToken })
    .then(({ data }) => {
      if (!data?.access) throw new Error('accessToken not found');
      Cookies.set('accessToken', data.access, { secure: true });
      if (data.refresh) Cookies.set('refreshToken', data.refresh, { secure: true });
      return data.access;
    })
    .finally(() => {
      mesaControlRefreshPromise = null;
    });

  return mesaControlRefreshPromise;
};

const configureMesaControlAuthentication = (instance, retryFlag) => {
  instance.interceptors.request.use((request) => {
    const accessToken = Cookies.get('accessToken');
    if (accessToken) {
      request.headers = request.headers || {};
      request.headers.Authorization = `Bearer ${accessToken}`;
    }
    return request;
  });

  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error?.config;
      if (
        error?.response?.status !== 401 ||
        !originalRequest ||
        originalRequest[retryFlag] ||
        /\/api\/v1\/auth\/refresh\/?(?:\?|$)/.test(originalRequest.url || '')
      ) {
        return Promise.reject(error);
      }

      originalRequest[retryFlag] = true;
      try {
        const accessToken = await refreshMesaControlAccessToken();
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        return instance(originalRequest);
      } catch (refreshError) {
        Cookies.remove('refreshToken');
        Cookies.remove('accessToken');
        Cookies.remove('user');
        return Promise.reject(refreshError);
      }
    }
  );
};

configureMesaControlAuthentication(axiosOriginationInstance, '_originationAuthRetried');
configureMesaControlAuthentication(axiosProscaiReportsInstance, '_proscaiReportsAuthRetried');

export const axiosSgcInstance = axios.create({
  baseURL: CHATBOT_HOST_API,
  headers: {
    Authorization: `Token ${CHATBOT_API_KEY}`,
  },
});
